import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { MarketService } from "../../../services/MarketService";
import { mdlTablePagination } from "../../../models/ui-models/TablePagination";
import { PageHeaderSection } from "../../../components/common/PageHeadSection";
import { Skeleton } from "antd";
import { MarketTable } from "../../../components/market/MarketTable";
import { SFModal } from "../../../components/elements/SFModal";
import { MarketDetail } from "../../../components/market/MarketDetail";
import { mdlMarketListRequest } from "../../../models/service-models/market/MarketListRequest";
import { HotelSelect } from "../../../components/common/HotelSelect";
import { mdlMarketDeleteRequest } from "../../../models/service-models/market/MarketDeleteRequest";
import { toast } from "react-toastify";
import { ObjectHelper } from "../../../utils/ObjectHelper";
import { mdlMarket } from "../../../models/domain/Market";
import { HeaderButton } from "../../../models/ui-models/HeaderButton";
import { faCopy, faFileExcel, faFilePdf, faPlus } from "@fortawesome/free-solid-svg-icons";
import { CopyMarketToOtherHotels } from "../../../components/definitions/market/CopyMarketToOtherHotels";
import { ExportModal } from "../../../components/common/ExportModal";
import { enmExportModalType } from "../../../models/enums/ExportModalType";
import { enmExcelExportType } from "../../../utils/ExcelColumnMappings";


export const MarketDefinition = () => {
    const { t } = useTranslation();
    const [openDetailModal, setOpenDetailModal] = useState<boolean>(false);
    const [selectedMarket, setSelectedMarket] = useState<string>();
    const [selectedHotel, setSelectedHotel] = useState<string>();
    const [request, setRequest] = useState<mdlMarketListRequest>({ pageNumber: 1, pageSize: 10 });
    const [openModalCopyToOtherHotels, setOpenModalCopyToOtherHotels] = useState<boolean>(false);
    const [rowSelectionIds, setRowSelectionIds] = useState<string[]>([]);
    const [openModalExcelExport, setOpenModalExcelExport] = useState<boolean>(false);
    const [openModalPDFExport, setOpenModalPDFExport] = useState<boolean>(false);

    useEffect(() => {
        if (!data && selectedHotel)
            refetch();
    }, [selectedHotel]);


    const {
        data,
        isLoading,
        refetch
    } = useQuery(
        ["getMarkets", selectedHotel, request],
        () => {
            if (selectedHotel)
                return MarketService.List({ ...request, hotelId: selectedHotel });
        },
        {
            refetchOnWindowFocus: false,
            enabled: !!selectedHotel
        }
    );

    const handleHotelSelect = (hotelId: string) => {
        setRequest({ pageNumber: 1, pageSize: 10 })
        setSelectedHotel(hotelId);
    }

    const handleTableParamsChange = (pagination: mdlTablePagination, filters: any) => {
        setRequest({ ...request, pageSize: pagination.pageSize ?? 10, pageNumber: pagination.current ?? 1 })
    }

    const handleEdit = (marketId: string) => {
        setSelectedMarket(marketId);
        setOpenDetailModal(true);
    }

    const handleDelete = (marketId: string) => {
        deleteMarket({ id: marketId });
    }

    const exportToPDF = () => {
        setOpenModalPDFExport(true);
    }

    const { mutateAsync: deleteMarket, isLoading: mutateLoading, data: mutateData } = useMutation(
        (request: mdlMarketDeleteRequest) => MarketService.Delete(request),
        {
            onMutate(variables) {
            },
            onError: error => {
                console.log(error);
            },
            onSuccess(data, variables, context) {
                if (data.header && data.header.success) {
                    toast.success(t("common.success"));
                    refetch();
                }
                else {
                    toast.error(ObjectHelper.getErrorMessage(data.header.messages));
                }
            },
        }
    );

    const handleAddNew = () => {
        setSelectedMarket(undefined);
        setOpenDetailModal(true);
    }

    const handleDetailCallback = () => {
        setOpenDetailModal(false);
        refetch();
    }

    const copyToOtherHotels = () => {
        if (rowSelectionIds.length > 0) {
            setOpenModalCopyToOtherHotels(true);
        }
        else {
            toast.error(t("common.pleaseSelectAtLeastOneRecord"));
        }
    }

    const getButtons = (): HeaderButton[] | undefined => {
        let _buttons: HeaderButton[] = [];
        if (selectedHotel) {
            _buttons.push({
                title: t("common.addNew"),
                icon: faPlus,
                fnFunction: handleAddNew,
                className: "btn btn-white btn-dim btn-outline-primary"
            });
            _buttons.push({
                title: t("definitions.hotels.copyToOtherHotels"),
                icon: faCopy,
                fnFunction: copyToOtherHotels,
                className: "btn btn-white btn-dim btn-outline-primary"
            });
            _buttons.push({
                title: t("common.exportToExcel"),
                icon: faFileExcel,
                fnFunction: exportToExcel,
                className: "btn btn-white btn-dim btn-outline-primary"
            });
            _buttons.push({
                title: t("common.pdf"),
                icon: faFilePdf,
                fnFunction: exportToPDF,
                className: "btn btn-white btn-dim btn-outline-primary"
            });
        }
        return _buttons;
    }

    const exportToExcel = () => {
        setOpenModalExcelExport(true);
    }

    const handleCopyNotConfirmsToOtherHotelsCallback = () => {
        setOpenModalCopyToOtherHotels(false);
        refetch();
    }

    return (
        <>
            <div className="nk-content ">
                <div className="container-fluid">
                    <div className="nk-content-inner">
                        <div className="nk-content-body">
                            <PageHeaderSection
                                pageTitle={t("definitions.markets.title")}
                                buttons={getButtons()}
                            />
                            <div className="nk-block">
                                <div className="row g-gs">
                                    <div className="col-12">
                                        <div className="card card-bordered h-100">
                                            <div className="card-inner">
                                                {
                                                    <HotelSelect onChange={handleHotelSelect} />
                                                }
                                                {
                                                    isLoading
                                                    &&
                                                    <>
                                                        <Skeleton active />
                                                        <Skeleton active />
                                                        <Skeleton active />
                                                    </>
                                                }
                                                {
                                                    data
                                                    &&
                                                    <MarketTable
                                                        onDelete={handleDelete}
                                                        onEdit={handleEdit}
                                                        pagination={{
                                                            align: "center",
                                                            current: data?.body?.pageNumber,
                                                            pageSizeOptions: [10, 20, 50],
                                                            defaultPageSize: 10,
                                                            pageSize: request.pageSize,
                                                            defaultCurrent: 1,
                                                            responsive: true,
                                                            showSizeChanger: true,
                                                            total: data?.body?.resultCount,
                                                            showTotal: (total: number, range: [number, number]) => t("pagination.total") + ` ${data?.body?.resultCount} ` + t("pagination.result"),
                                                            locale: { items_per_page: "/ " + t("pagination.itemPerPage") }
                                                        }}

                                                        onTableParamsChange={handleTableParamsChange}
                                                        dataSource={data?.body?.data}
                                                        rowSelectionChange={(hotelNotConfirms: mdlMarket[]) => setRowSelectionIds(hotelNotConfirms?.map(x => x.id))}
                                                    />
                                                }
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <SFModal
                width={window.innerWidth * 30 / 100}
                open={openModalExcelExport}
                toggler={() => { setOpenModalExcelExport(!openModalExcelExport) }}
                body={<ExportModal excelExportType={enmExcelExportType.Market} name="marketDefinitions" data={data?.body?.data} fnModalClose={() => setOpenModalExcelExport(!openModalExcelExport)} />}
                title={t("common.exportToExcel")}
            />
            <SFModal
                width={window.innerWidth * 30 / 100}
                open={openModalPDFExport}
                toggler={() => { setOpenModalPDFExport(!openModalPDFExport) }}
                body={<ExportModal excelExportType={enmExcelExportType.Market} name="marketDefinitions" type={enmExportModalType.PDFTable} data={data?.body?.data} fnModalClose={() => setOpenModalPDFExport(!openModalPDFExport)} />}
                title={t("common.exportToPDF")}
            />
            <SFModal
                width={window.innerWidth * 50 / 100}
                open={openDetailModal}
                toggler={() => { setOpenDetailModal(!openDetailModal) }}
                body={<MarketDetail id={selectedMarket} hotelId={selectedHotel} callback={handleDetailCallback} />}
                title={t("definitions.markets.detailTitle")}
            />
            <SFModal
                open={openModalCopyToOtherHotels}
                toggler={() => { setOpenModalCopyToOtherHotels(!openModalCopyToOtherHotels) }}
                body={<CopyMarketToOtherHotels marketIds={rowSelectionIds} fromHotelId={selectedHotel} callback={handleCopyNotConfirmsToOtherHotelsCallback} />}
                title={t("definitions.hotels.copyToOtherHotels")}
            />
        </>
    );
};
